import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Plus, Sparkles, Copy } from "lucide-react";
import { api, streamPost } from "@/lib/api";

export default function ReviewsManager() {
  const [reviews, setReviews] = useState([]);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = () =>
    api
      .get("/admin/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await api.post("/admin/reviews", { author, text });
      setReviews((rs) => [data, ...rs]);
      setAuthor("");
      setText("");
      toast.success("Recensione pubblicata");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (r) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${r.id}`, { visible: !r.visible });
      setReviews((rs) => rs.map((x) => (x.id === r.id ? data : x)));
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const remove = async (r) => {
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      setReviews((rs) => rs.filter((x) => x.id !== r.id));
      toast.success("Recensione eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const reply = async (r) => {
    setDrafts((d) => ({ ...d, [r.id]: { text: "", loading: true } }));
    try {
      await streamPost("/admin/ai/review-reply", { author: r.author, text: r.text }, (t) =>
        setDrafts((d) => ({ ...d, [r.id]: { text: (d[r.id]?.text || "") + t, loading: true } }))
      );
      setDrafts((d) => ({ ...d, [r.id]: { text: d[r.id]?.text || "", loading: false } }));
    } catch {
      setDrafts((d) => ({ ...d, [r.id]: { text: "", loading: false } }));
      toast.error("Errore AI: riprova tra poco");
    }
  };

  const copyDraft = (id) => {
    navigator.clipboard.writeText(drafts[id]?.text || "");
    toast.success("Risposta copiata: incollala su Google");
  };

  return (
    <div data-testid="admin-reviews-manager">
      <h1 className="font-display text-3xl font-semibold text-ink sm:text-4xl">Recensioni 5 stelle</h1>
      <p className="mt-2 text-sm text-stone-500">
        Copia qui le recensioni a 5 stelle da Google: solo quelle &ldquo;visibili&rdquo; appaiono sul sito. Con
        &ldquo;Rispondi con AI&rdquo; generi la bozza di risposta da pubblicare su Google.
      </p>

      <form onSubmit={add} className="mt-8 rounded-2xl border border-bordeaux/15 bg-white p-5 sm:p-7">
        <input
          data-testid="admin-review-author-input"
          type="text"
          required
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Nome autore (es. Marco T.)"
          className="mb-3 w-full rounded-xl border border-bordeaux/20 px-4 py-3 text-sm outline-none focus:border-bordeaux"
        />
        <textarea
          data-testid="admin-review-text-input"
          required
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Testo della recensione…"
          className="mb-4 w-full rounded-xl border border-bordeaux/20 px-4 py-3 text-sm outline-none focus:border-bordeaux"
        />
        <button
          data-testid="admin-review-add-button"
          type="submit"
          disabled={adding}
          className="inline-flex items-center gap-2 rounded-full bg-bordeaux px-7 py-3 font-mono-alt text-[11px] uppercase tracking-[0.2em] text-ivory transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
        >
          <Plus className="h-4 w-4" /> {adding ? "Pubblicazione…" : "Aggiungi recensione"}
        </button>
      </form>

      {loading ? (
        <p className="mt-8 font-mono-alt text-xs uppercase tracking-[0.25em] text-stone-400">Caricamento…</p>
      ) : (
        <div className="mt-8 space-y-4">
          {reviews.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-review-row-${r.id}`}
              className={`rounded-2xl border bg-white p-5 sm:p-6 ${r.visible ? "border-bordeaux/20" : "border-stone-200 opacity-60"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-ink">{r.author}</span>
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.18em] text-stone-400">{r.date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid={`admin-review-ai-reply-${r.id}`}
                    onClick={() => reply(r)}
                    disabled={drafts[r.id]?.loading}
                    className="inline-flex items-center gap-1.5 rounded-full border border-bordeaux/25 px-4 py-1.5 font-mono-alt text-[10px] uppercase tracking-[0.18em] text-bordeaux transition-colors hover:bg-bordeaux hover:text-ivory disabled:opacity-50"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Rispondi con AI
                  </button>
                  <label className="flex items-center gap-2 text-xs text-stone-500">
                    <input
                      data-testid={`admin-review-visible-${r.id}`}
                      type="checkbox"
                      checked={r.visible}
                      onChange={() => toggle(r)}
                      className="h-4 w-4 accent-[#AB182B]"
                    />
                    Visibile
                  </label>
                  <button
                    data-testid={`admin-review-delete-${r.id}`}
                    onClick={() => remove(r)}
                    className="rounded-full border border-bordeaux/25 p-2 text-bordeaux transition-colors hover:bg-bordeaux hover:text-ivory"
                    aria-label="Elimina recensione"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-stone-600">{r.text}</p>
              {drafts[r.id] && (drafts[r.id].text || drafts[r.id].loading) && (
                <div className="mt-4 rounded-xl border border-bordeaux/15 bg-ivory-light/70 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-mono-alt text-[9px] uppercase tracking-[0.22em] text-bordeaux">
                      Bozza di risposta AI — da rivedere
                    </span>
                    {drafts[r.id].text && !drafts[r.id].loading && (
                      <button
                        data-testid={`admin-review-copy-reply-${r.id}`}
                        onClick={() => copyDraft(r.id)}
                        className="inline-flex items-center gap-1 font-mono-alt text-[9px] uppercase tracking-[0.18em] text-bordeaux hover:underline"
                      >
                        <Copy className="h-3 w-3" /> Copia
                      </button>
                    )}
                  </div>
                  <p
                    data-testid={`admin-review-reply-text-${r.id}`}
                    className="whitespace-pre-wrap font-display text-base italic leading-relaxed text-stone-700"
                  >
                    {drafts[r.id].text || <span className="animate-pulse text-stone-400">Generazione in corso…</span>}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
